import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "../../lib/animations";
import { PHOTOS } from "../../lib/site";

const CHAPTERS = [
  {
    no: "01",
    title: "A life devoted to the dance",
    body: "Cibele has dedicated her life to belly dancing — living in iconic cities such as Athens and Cairo, and spending extended periods in Istanbul, where she carved out a prominent place within the influential Romani community.",
  },
  {
    no: "02",
    title: "A Balkan Romani voice in Europe",
    body: "She developed her career as a Balkan Romani dancer in Europe. Her unique, energetic and deeply emotional style has won over audiences across the continent and far beyond.",
  },
  {
    no: "03",
    title: "Explorer, writer, entrepreneur",
    body: "Beyond the stage, Cibele is an oriental explorer and writer — collaborating with Balkan musicians, writing about the culture she loves, and building a brand around beauty, movement and freedom.",
  },
];

export const About = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const istY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-champagne px-6 py-28 md:px-12 md:py-40"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-24">
        {/* Sticky heading + portrait */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <Reveal>
              <p className="mb-6 flex items-center gap-3 font-sans text-[0.7rem] uppercase tracking-[0.34em] text-terracotta">
                <span className="h-px w-10 bg-gold" /> The Manifesto
              </p>
              <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-espresso md:text-6xl">
                BellyDance is a{" "}
                <span className="italic text-plum">lifestyle</span>, not a
                performance.
              </h2>
              <p className="mt-8 max-w-md font-sans text-base leading-relaxed text-espresso/70">
                From the temples of Athens to the backstreets of Istanbul, every
                movement carries a story of migration, femininity and freedom.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 overflow-hidden rounded-t-[160px] border border-gold/40">
                <img
                  src={PHOTOS.portrait}
                  alt="Portrait of Cibele"
                  className="aspect-[4/5] w-full object-cover object-top"
                />
              </div>
            </Reveal>
          </div>
        </div>

        {/* Chapters + editorial image */}
        <div className="lg:col-span-7">
          <div className="flex flex-col">
            {CHAPTERS.map((c, i) => (
              <Reveal
                key={c.no}
                delay={i * 0.05}
                className="border-t border-espresso/15 py-10 first:border-t-0 first:pt-0"
              >
                <div className="flex gap-6 md:gap-10">
                  <span className="font-serif text-2xl italic text-gold md:text-3xl">
                    {c.no}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-espresso md:text-3xl">
                      {c.title}
                    </h3>
                    <p className="mt-4 max-w-xl font-sans text-base leading-relaxed text-espresso/70">
                      {c.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Istanbul editorial image with parallax */}
          <Reveal className="mt-12">
            <div
              ref={imgRef}
              className="relative overflow-hidden rounded-3xl border border-espresso/10"
            >
              <div className="aspect-[16/10] w-full overflow-hidden">
                <motion.img
                  style={{ y: istY, scale: 1.12 }}
                  src={PHOTOS.istanbul}
                  alt="Cibele on a rooftop overlooking Istanbul"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-espresso/50 to-transparent" />
              <p className="absolute bottom-6 left-6 font-serif text-xl italic text-champagne md:text-2xl">
                Istanbul — where the story took flight.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
